import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { stripePromise } from '../../../utils/stripe';
import { Country, State } from 'country-state-city';
import { useNetwork } from '../../../utils/network';
import { useUser } from '../../../contexts/UserContext';
import { track } from '../../../utils/track';
import AuctionPanel from '../../Auction/AuctionPanel';
import {
  PT,
  fieldSx,
  primaryBtnSx,
  ghostBtnSx,
  panelSx,
  kickerSx
} from '../../../components/portalUi';
import { selectMenuProps, selectSx } from '../../../components/portalSelect';
import angelicPack from './assets/angelic-pack.png';

// Default Stripe.js = the shared toggle-aware instance (utils/stripe). A physical-card
// payment overrides it with the server-returned publishableKey via `activeStripe` below.

// The Angelic accent — a warm gold over the portal's dark-teal ground.
const GOLD = '#e8c877';
const GOLD_SOFT = 'rgba(232,200,119,0.14)';
const GOLD_BORDER = 'rgba(232,200,119,0.5)';

// Scarcity accent for the "almost sold out" badge — a warmer amber than the
// Angelic gold so it reads as urgency rather than decoration.
const URGENT = '#f2a24b';
const URGENT_SOFT = 'rgba(242,162,75,0.15)';
const URGENT_BORDER = 'rgba(242,162,75,0.55)';
// The whole print run shares one pack cap; surface "almost sold out" once stock
// is down to this share of the cap. Admin-tunable.
const ALMOST_SOLD_OUT_AT = 0.1; // <= 10% of the pack cap remaining
// Admin wants the scarcity badge shown NOW (marketing), ahead of real stock
// reaching the threshold above. Set to false to make it purely stock-driven.
const FORCE_ALMOST_SOLD_OUT = true;

// Value props for the storefront landing — what makes these cards worth pre-buying.
const FEATURES: Array<{ icon: React.ReactNode; title: string; body: string }> =
  [
    {
      icon: <AutoAwesomeIcon fontSize="small" />,
      title: 'Limited first run',
      body: "A one-time print — once it's gone, it's gone."
    },
    {
      icon: <DesignServicesIcon fontSize="small" />,
      title: 'Custom-made',
      body: 'Designed and printed exclusively for INCRYPT.'
    },
    {
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
      title: 'Shipped to you',
      body: 'Boxed and mailed once the print run is ready.'
    }
  ];

type OptionId = 'pack_1' | 'pack_10' | 'collectors_box';

const OPTIONS: Array<{
  id: OptionId;
  name: string;
  price: number;
  tagline: string;
  includes: string[];
  featured?: boolean;
  boxOnly?: boolean;
  fan?: number; // show this many packs fanned out (conveys the bundle size)
}> = [
  {
    id: 'pack_1',
    name: '1 Pack',
    price: 69.99,
    tagline: '10 cards',
    includes: ['One Angelic Faction pack', '10 physical cards']
  },
  {
    id: 'pack_10',
    name: '10 Pack',
    price: 699.9,
    tagline: '100 cards + Incrypt Display Case',
    includes: [
      'Ten Angelic Faction packs',
      '100 physical cards',
      'Incrypt Display Case included'
    ],
    featured: true,
    fan: 10
  },
  {
    /* The display case on its own ("Incrypt Display Case" since 2026-10-22, was "Incrypt
     * Card Holder" — the option code stays). `boxOnly` drives a different card body (no pack fan,
     * no card count) and, with PACKS_PER 0 below, keeps it out of the pack cap —
     * so it stays buyable after the packs sell out, which is the point of
     * offering it separately. Named to match the 10-pack copy: it is the same
     * item that ships inside that bundle. */
    id: 'collectors_box',
    name: 'Incrypt Display Case',
    price: 60,
    tagline: 'Display case only',
    includes: ['The Incrypt Display Case on its own', 'No packs included'],
    boxOnly: true
  }
];

// Prefix with A$ (not the bare "$" en-AU currency style gives) so it's unmistakably
// Australian dollars — the storefront prices are AUD and buyers were unsure.
const aud = (n: number) =>
  `A$${n.toLocaleString('en-AU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

type Shipping = {
  name: string;
  email: string;
  phone: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postcode: string;
  country: string;
};

const EMPTY_SHIPPING: Shipping = {
  name: '',
  email: '',
  phone: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postcode: '',
  country: 'AU'
};

type Step = 'choose' | 'shipping' | 'pay' | 'done';

/* 3D hover-tilt: the point under the cursor tips TOWARD the viewer (like inspecting
   a TCG card). Not a spin — a subtle parallax that follows the mouse and lifts. */
const Tilt: React.FC<{
  children: React.ReactNode;
  max?: number;
  sx?: SxProps<Theme>;
}> = ({ children, max = 16, sx }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tf, setTf] = useState('perspective(900px)');
  const onMove = (e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const mx = (e.clientX - r.left) / r.width;
    const my = (e.clientY - r.top) / r.height;
    const rx = (my - 0.5) * max; // hover lower  -> bottom edge toward viewer
    const ry = (0.5 - mx) * max; // hover to the left -> left edge toward viewer
    setTf(
      `perspective(900px) rotateX(${rx.toFixed(2)}deg) rotateY(${ry.toFixed(2)}deg) scale(1.2) translateY(-12px)`
    );
  };
  const reset = () =>
    setTf('perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)');
  return (
    <Box
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      sx={{
        transform: tf,
        transition: 'transform .1s ease-out',
        transformStyle: 'preserve-3d',
        willChange: 'transform',
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

/* A fanned spread of packs — shows the bundle size (you get 10) at a glance. */
const PackFan: React.FC<{ count: number }> = ({ count }) => {
  const mid = (count - 1) / 2;
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'grid',
        placeItems: 'center'
      }}
    >
      {Array.from({ length: count }).map((_, i) => {
        const off = i - mid;
        return (
          <Box
            key={i}
            component="img"
            src={angelicPack}
            alt=""
            sx={{
              position: 'absolute',
              height: 168,
              transformOrigin: 'bottom center',
              transform: `rotate(${off * 5.2}deg) translateX(${off * 3}px)`,
              zIndex: Math.round(50 - Math.abs(off) * 4),
              filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.55))'
            }}
          />
        );
      })}
    </Box>
  );
};

/* ── Payment step (inside <Elements>) ───────────────────────────────────────── */
const PaymentForm: React.FC<{
  amount: number;
  billing: {
    name: string;
    email: string;
    phone?: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state?: string;
      postal_code: string;
      country: string;
    };
  };
  onDone: () => void;
  onBack: () => void;
}> = ({ amount, billing, onDone, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pay = async () => {
    if (!stripe || !elements) return;
    setPaying(true);
    setErr(null);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
        // Reuse the shipping address as billing, so Stripe doesn't ask for it again.
        payment_method_data: { billing_details: billing }
      },
      redirect: 'if_required'
    });
    if (error) {
      setErr(error.message || 'Payment could not be completed.');
      setPaying(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      track('physical_card.paid', { amount });
      onDone();
      return;
    }
    // 3DS / processing — treat as submitted; the webhook confirms the order.
    onDone();
  };

  return (
    <Box>
      {/* Don't collect a billing address (incl. country) — we pass the shipping
          address as billing above. Just the card. */}
      <PaymentElement
        options={{ fields: { billingDetails: { address: 'never' } } }}
      />
      {err && (
        <Typography sx={{ mt: 1.5, color: '#ff8a9b', fontSize: 13 }}>
          {err}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5 }}>
        <Button
          onClick={onBack}
          disabled={paying}
          sx={{ ...ghostBtnSx, minWidth: 96 }}
        >
          Back
        </Button>
        <Button
          onClick={pay}
          disabled={!stripe || paying}
          sx={{ ...primaryBtnSx, flex: 1, py: 1.1 }}
        >
          {paying ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            `Pay ${aud(amount)}`
          )}
        </Button>
      </Box>
    </Box>
  );
};

/* ── Page ────────────────────────────────────────────────────────────────────── */
/* Packs each option consumes from the drop cap (mirrors the server's
 * PHYSICAL_CARD_PACKS). The holder is 0 — it is not a pack, so `soldOutFor` and
 * `maxQtyFor` both skip the stock maths for it. */
const PACKS_PER: Record<OptionId, number> = {
  pack_1: 1,
  pack_10: 10,
  collectors_box: 0
};
// Per-order quantity ceiling (mirrors the server's PHYSICAL_MAX_QUANTITY).
const MAX_QTY = 50;

export const PhysicalCards: React.FC = () => {
  const { get, post } = useNetwork();
  const { user } = useUser();
  const [step, setStep] = useState<Step>('choose');
  const [selectedId, setSelectedId] = useState<OptionId | null>(null);
  const [shipping, setShipping] = useState<Shipping>({
    ...EMPTY_SHIPPING,
    email: user?.email || ''
  });
  const [payment, setPayment] = useState<{
    clientSecret: string;
    amount: number;
    publishableKey?: string;
  } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [stock, setStock] = useState<{
    cap: number;
    remaining: number;
    soldOut: boolean;
  } | null>(null);
  const [hasSavedAddress, setHasSavedAddress] = useState(false);
  // "Pack my order on video for INCRYPT's socials" — an opt-in kept on the order
  const [packOnVideo, setPackOnVideo] = useState(false);
  const [quantity, setQuantity] = useState(1);
  // Saving the address is now a checkbox (ticked by default); on a successful
  // payment we persist it if still ticked. Replaces the old explicit Save button.
  const [saveAddressChecked, setSaveAddressChecked] = useState(true);
  // Live Australia Post shipping quote for the current option / qty / address.
  const [shippingQuote, setShippingQuote] = useState<{
    cost: number;
    service: string | null;
    source: string;
  } | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  const refreshStock = async () => {
    try {
      const res = await get('/checkout/physical-card-stock', true);
      if (res && typeof res.remaining === 'number') {
        setStock({
          cap: Number(res.cap) || 1250,
          remaining: Number(res.remaining),
          soldOut: !!res.soldOut
        });
      }
    } catch {
      /* non-fatal — the page still works without the stock badge */
    }
  };

  // On mount: load remaining stock + prefill a saved shipping address.
  useEffect(() => {
    refreshStock();
    (async () => {
      try {
        const res = await get('/checkout/shipping-details', true);
        if (res?.shipping) {
          setShipping((s) => ({ ...s, ...res.shipping }));
          setHasSavedAddress(true);
        }
      } catch {
        /* no saved address — fine */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = useMemo(
    () => OPTIONS.find((o) => o.id === selectedId) || null,
    [selectedId]
  );

  // Full country + state/province lists (country-state-city), same as the signup form.
  const countries = useMemo(() => Country.getAllCountries(), []);
  const states = useMemo(
    () => State.getStatesOfCountry(shipping.country) || [],
    [shipping.country]
  );

  // Init Stripe.js with the account the server put THIS PaymentIntent on — usually the
  // primary account, but the legacy account when the primary is flagged and the server
  // failed over (a legacy client_secret only works with the legacy publishable key).
  // Falls back to the primary env key when the server didn't send one.
  const activeStripe = useMemo(
    () => (payment?.publishableKey ? loadStripe(payment.publishableKey) : stripePromise),
    [payment?.publishableKey]
  );

  const soldOutFor = (id: OptionId) =>
    !!stock && PACKS_PER[id] > 0 && stock.remaining < PACKS_PER[id];

  // Store-wide scarcity flag (all options draw from one pack cap) — shown per
  // card for urgency once stock dips under ALMOST_SOLD_OUT_AT of the cap.
  const almostSoldOut =
    FORCE_ALMOST_SOLD_OUT ||
    (!!stock &&
      stock.remaining > 0 &&
      stock.remaining <= Math.ceil(stock.cap * ALMOST_SOLD_OUT_AT));

  // Max quantity for an option: capped by MAX_QTY and, for pack options, by how
  // many packs are actually left (mirrors the server's cap check).
  const maxQtyFor = (id: OptionId): number => {
    const per = PACKS_PER[id];
    if (per <= 0) return MAX_QTY; // box: doesn't draw from the pack cap
    const byStock = stock ? Math.floor(stock.remaining / per) : MAX_QTY;
    return Math.max(1, Math.min(MAX_QTY, byStock));
  };
  const maxQty = selected ? maxQtyFor(selected.id) : MAX_QTY;

  // Live shipping quote: re-fetch (debounced) whenever the option, quantity, or the
  // destination country/postcode changes. The server re-quotes authoritatively at
  // payment time; this is the on-screen preview so the total updates as they type.
  useEffect(() => {
    if (!selected) return;
    const country = shipping.country.trim().toUpperCase();
    const postcode = shipping.postcode.trim();
    if (!country || (country === 'AU' && !postcode)) {
      setShippingQuote(null);
      setQuoteLoading(false); // clear a spinner left over from a prior valid entry
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await post(
          '/checkout/physical-card-shipping-quote',
          { option: selected.id, quantity, shipping: { country, postcode } },
          true
        );
        if (!cancelled && res && typeof res.shipping === 'number') {
          setShippingQuote({
            cost: res.shipping,
            service: res.service || null,
            source: res.source
          });
        }
      } catch {
        if (!cancelled) setShippingQuote(null);
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, quantity, shipping.country, shipping.postcode]);

  // Keep the chosen quantity within [1, maxQty] as the selection / stock change.
  useEffect(() => {
    setQuantity((q) => Math.min(Math.max(1, q), maxQty));
  }, [maxQty]);

  const set =
    (k: keyof Shipping) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setShipping((s) => ({ ...s, [k]: e.target.value }));

  const choose = (id: OptionId) => {
    setSelectedId(id);
    setQuantity(1);
    setFormError(null);
    setStep('shipping');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submitShipping = async () => {
    if (!selected) return;
    const required: Array<keyof Shipping> = [
      'name',
      'email',
      'phone',
      'line1',
      'city',
      'postcode',
      'country'
    ];
    // State is required when the chosen country actually has states to pick from.
    if (states.length > 0) required.push('state');
    const missing = required.filter((k) => !shipping[k].trim());
    if (missing.length) {
      setFormError('Please fill in every required field.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(shipping.email.trim())) {
      setFormError('Please enter a valid email address.');
      return;
    }
    setFormError(null);
    setSubmitting(true);
    try {
      const attemptId =
        (window.crypto &&
          window.crypto.randomUUID &&
          window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const res = await post(
        '/checkout/physical-card-payment-intent',
        { option: selected.id, shipping, attemptId, quantity, packOnVideo },
        true
      );
      if (!res?.clientSecret) throw new Error('No client secret returned.');
      setPayment({
        clientSecret: res.clientSecret,
        amount: Number(res.amount) || selected.price,
        publishableKey: res.publishableKey
      });
      track('physical_card.checkout_started', { option: selected.id });
      setStep('pay');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      setFormError(
        e?.message || 'Could not start the payment. Please try again.'
      );
      refreshStock(); // a SOLD_OUT rejection means the remaining count changed
    }
    setSubmitting(false);
  };

  // Best-effort, fire-and-forget: persist the address after a successful payment
  // if the user left the "save" checkbox ticked. Failures are swallowed so they
  // never block the confirmation screen.
  const persistShippingAddress = async () => {
    try {
      await post('/checkout/shipping-details', { shipping }, true);
      setHasSavedAddress(true);
    } catch {
      /* best-effort */
    }
  };
  const handlePaymentDone = () => {
    if (saveAddressChecked) void persistShippingAddress();
    setStep('done');
  };

  return (
    <Box
      sx={{
        maxWidth: 1120,
        mx: 'auto',
        px: { xs: 2, md: 3 },
        py: { xs: 2, md: 4 },
        color: PT.text,
        fontFamily: 'Montserrat, sans-serif'
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 } }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: 28, md: 40 },
            lineHeight: 1.1,
            letterSpacing: '-0.01em',
            background: `linear-gradient(180deg, #fff, ${GOLD})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Incrypt Physical Cards
        </Typography>
        <Typography
          sx={{
            mt: 1.25,
            color: GOLD,
            fontWeight: 700,
            fontSize: { xs: 15, md: 17 },
            letterSpacing: '0.01em'
          }}
        >
          Pre-purchase limited Angelic Faction packs now
        </Typography>
      </Box>

      {/* Scarcity banner — one line across the top of the drop rather than a number
          on each card. On the cards it read as a per-product label; here it reads as
          what it is, a countdown on the whole run.

          Hidden once sold out: the cards say so themselves, and "0 packs left" is
          shouting at a closed door. Hidden too until stock has loaded, so the page
          never flashes a figure it is about to correct. */}
      {/* The physical card-set auction, above the store while one is on (it renders
          nothing otherwise). PaymentForm is handed in: the panel must not import
          this file, which imports it. */}
      {step === 'choose' && (
        <Box
          sx={{
            '&:not(:empty)': {
              pb: { xs: 5, md: 7 },
              mb: { xs: 5, md: 7 },
              borderBottom: '1px solid rgba(140,170,190,0.14)'
            }
          }}
        >
          <AuctionPanel PaymentForm={PaymentForm} />
        </Box>
      )}

      {step === 'choose' && stock && !stock.soldOut && stock.remaining > 0 && (
        <Box
          sx={{
            position: 'relative',
            isolation: 'isolate',
            mb: 4,
            pt: { xs: 3, md: 4 },
            pb: { xs: 3, md: 4 },
            px: 2,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: -1,
              pointerEvents: 'none',
              background:
                'radial-gradient(60% 90% at 50% 55%, rgba(242,162,75,0.22), rgba(242,162,75,0.06) 55%, transparent 100%)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              mb: 1.5,
              '&::before, &::after': {
                content: '""',
                height: '1px',
                width: { xs: 34, md: 72 },
                background: 'linear-gradient(90deg, transparent, rgba(242,162,75,0.75))'
              },
              '&::after': {
                background: 'linear-gradient(90deg, rgba(242,162,75,0.75), transparent)'
              }
            }}
          >
            <Typography
              sx={{
                font: '800 11px ui-monospace, Menlo, Consolas, monospace',
                letterSpacing: { xs: '0.2em', sm: '0.32em' },
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
                color: '#f2a24b'
              }}
            >
              First run · almost sold out
            </Typography>
          </Box>
          <Typography
            component="h2"
            sx={{
              textAlign: 'center',
              mt: 0,
              mb: 2.5,
              mx: 'auto',
              px: 2,
              maxWidth: 760,
              color: '#fff',
              fontWeight: 800,
              fontSize: { xs: 24, sm: 32, md: 40 },
              lineHeight: 1.15,
              letterSpacing: '0.01em',
              textWrap: 'balance',
              textShadow: '0 4px 40px rgba(242,162,75,0.35)'
            }}
          >
            Don&rsquo;t miss out on your chance to get the{' '}
            <Box
              component="span"
              sx={{
                color: URGENT,
                whiteSpace: { xs: 'normal', md: 'nowrap' },
                backgroundImage: 'linear-gradient(90deg, #ffd27a, #f2a24b)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              last few remaining packs
            </Box>
            .
          </Typography>
          <Box
            sx={{
              mx: 'auto',
              mb: 0,
              maxWidth: 720,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
              textAlign: 'center',
              px: { xs: 2, md: 3 },
              py: { xs: 1.4, md: 1.75 },
              borderRadius: 2,
              background: `linear-gradient(180deg, ${URGENT_SOFT}, rgba(242,162,75,0.05))`,
              border: `1px solid ${URGENT_BORDER}`,
              boxShadow: '0 0 26px rgba(242,162,75,0.18)'
            }}
          >
            <WhatshotIcon sx={{ fontSize: 24, color: URGENT }} />
            <Typography
              sx={{
                fontWeight: 900,
                fontSize: { xs: 18, md: 22 },
                color: URGENT,
                letterSpacing: '0.01em'
              }}
            >
              Only {stock.remaining} pack{stock.remaining === 1 ? '' : 's'} left
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: 13, md: 14 },
                color: 'rgba(255,255,255,0.72)'
              }}
            >
              Once they&rsquo;re gone, they&rsquo;re gone.
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Step: choose ─────────────────────────────────────────────────────── */}
      {step === 'choose' && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
            alignItems: 'stretch'
          }}
        >
          {OPTIONS.map((o) => (
            <Box
              key={o.id}
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '18px',
                overflow: 'visible', // let the pack pop OUT of the card on hover
                border: `1px solid ${o.featured ? GOLD_BORDER : PT.border}`,
                background: o.featured
                  ? `radial-gradient(120% 80% at 50% -10%, ${GOLD_SOFT}, transparent 55%), ${PT.surface}`
                  : PT.surface,
                boxShadow: o.featured
                  ? '0 20px 50px -20px rgba(232,200,119,0.35)'
                  : 'none',
                transition: 'transform .18s ease, border-color .18s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  borderColor: GOLD_BORDER,
                  zIndex: 4
                }
              }}
            >
              {o.featured && (
                // corner clip so the diagonal ribbon stays a triangle even though the
                // card now overflows (needed for the pop-out)
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    width: 128,
                    height: 128,
                    overflow: 'hidden',
                    borderTopRightRadius: '18px',
                    zIndex: 5,
                    pointerEvents: 'none'
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 18,
                      right: -34,
                      transform: 'rotate(45deg)',
                      background: GOLD,
                      color: '#231a00',
                      fontWeight: 900,
                      fontSize: 10,
                      letterSpacing: '0.14em',
                      px: 4,
                      py: 0.5,
                      boxShadow: '0 3px 10px rgba(0,0,0,0.35)'
                    }}
                  >
                    BEST VALUE
                  </Box>
                </Box>
              )}

              {/* Art */}
              <Box
                sx={{
                  height: 260,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '18px 18px 0 0',
                  background:
                    'radial-gradient(80% 80% at 50% 30%, rgba(232,200,119,0.10), transparent 70%)'
                }}
              >
                {o.boxOnly ? (
                  <Box sx={{ textAlign: 'center', color: PT.textMuted }}>
                    <Inventory2Icon
                      sx={{ fontSize: 74, color: GOLD, opacity: 0.85 }}
                    />
                    <Typography
                      sx={{ mt: 1, fontSize: 11, letterSpacing: '0.1em' }}
                    >
                      BOX ART COMING SOON
                    </Typography>
                  </Box>
                ) : o.fan ? (
                  <Tilt
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'grid',
                      placeItems: 'center'
                    }}
                  >
                    <PackFan count={o.fan} />
                  </Tilt>
                ) : (
                  <Tilt
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'grid',
                      placeItems: 'center'
                    }}
                  >
                    <Box
                      component="img"
                      src={angelicPack}
                      alt="Angelic Faction pack"
                      sx={{
                        height: 240,
                        maxWidth: '80%',
                        objectFit: 'contain',
                        display: 'block',
                        filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.5))'
                      }}
                    />
                  </Tilt>
                )}
              </Box>

              {/* Body */}
              <Box
                sx={{
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1
                }}
              >
                {/* Scarcity is a PACK fact. The holder does not draw from the
                    pack cap and never sells out with them, so badging it
                    "almost sold out" would be urgency we cannot back up. */}
                {almostSoldOut && PACKS_PER[o.id] > 0 && !soldOutFor(o.id) && (
                  <Box
                    sx={{
                      alignSelf: 'center',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.6,
                      mb: 1.25,
                      px: 1.5,
                      py: 0.6,
                      borderRadius: '999px',
                      background: URGENT_SOFT,
                      border: `1px solid ${URGENT_BORDER}`,
                      color: URGENT,
                      fontWeight: 800,
                      fontSize: 12.5,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    <WhatshotIcon sx={{ fontSize: 16 }} />
                    Almost sold out
                  </Box>
                )}
                <Typography sx={{ fontWeight: 800, fontSize: 20 }}>
                  {o.name}
                </Typography>
                <Typography
                  sx={{
                    color: GOLD,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase'
                  }}
                >
                  {o.tagline}
                </Typography>
                <Typography sx={{ mt: 1.5, fontWeight: 900, fontSize: 30 }}>
                  {aud(o.price)}
                </Typography>
                <Box
                  component="ul"
                  sx={{ listStyle: 'none', p: 0, m: '14px 0 0', flex: 1 }}
                >
                  {o.includes.map((line) => (
                    <Box
                      component="li"
                      key={line}
                      sx={{
                        display: 'flex',
                        gap: 1,
                        alignItems: 'flex-start',
                        mb: 0.75,
                        color: PT.textMuted,
                        fontSize: 13.5
                      }}
                    >
                      <CheckCircleIcon
                        sx={{
                          fontSize: 16,
                          color: GOLD,
                          mt: '2px',
                          flexShrink: 0
                        }}
                      />
                      {line}
                    </Box>
                  ))}
                </Box>
                <Button
                  disabled={soldOutFor(o.id)}
                  onClick={() => choose(o.id)}
                  sx={{
                    mt: 2,
                    py: 1.05,
                    fontWeight: 800,
                    textTransform: 'none',
                    borderRadius: '10px',
                    ...(o.featured
                      ? {
                          background: `linear-gradient(180deg, ${GOLD}, #cfa947)`,
                          color: '#231a00',
                          '&:hover': {
                            background: `linear-gradient(180deg, #f2d98a, ${GOLD})`
                          }
                        }
                      : ghostBtnSx)
                  }}
                >
                  {soldOutFor(o.id) ? 'Sold out' : 'Pre-purchase'}
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Description + feature bubbles, below the packs */}
      {step === 'choose' && (
        <Box sx={{ mt: { xs: 3, md: 4 } }}>
          <Typography
            sx={{
              color: PT.textMuted,
              maxWidth: 720,
              mx: 'auto',
              textAlign: 'center',
              fontSize: 15,
              lineHeight: 1.6
            }}
          >
            Reserve the first run of real, physical INCRYPT trading cards — a{' '}
            <b style={{ color: PT.text }}>limited, custom-made</b> series of
            cards, printed only for this drop. Every pack holds{' '}
            <b style={{ color: PT.text }}>10 cards</b> of the Angelic Faction.
            This is a pre-purchase; orders ship once the first print run is
            complete.
          </Typography>
          <Box
            sx={{
              mt: { xs: 2.5, md: 3.5 },
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)'
              },
              gap: 1.5
            }}
          >
            {FEATURES.map((f) => (
              <Box
                key={f.title}
                sx={{
                  display: 'flex',
                  gap: 1.25,
                  p: 1.75,
                  borderRadius: '14px',
                  border: `1px solid ${PT.border}`,
                  background: 'rgba(255,255,255,0.02)'
                }}
              >
                <Box sx={{ color: GOLD, mt: '1px', display: 'flex' }}>
                  {f.icon}
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: PT.text,
                      lineHeight: 1.2
                    }}
                  >
                    {f.title}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.5,
                      fontSize: 12.5,
                      color: PT.textMuted,
                      lineHeight: 1.35
                    }}
                  >
                    {f.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* ── Step: shipping / pay ─────────────────────────────────────────────── */}
      {(step === 'shipping' || step === 'pay') && selected && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 340px' },
            gap: 3,
            alignItems: 'start'
          }}
        >
          {/* Left: form / payment */}
          <Box sx={{ ...panelSx, p: { xs: 2, md: 3 } }}>
            <Button
              onClick={() => setStep(step === 'pay' ? 'shipping' : 'choose')}
              startIcon={<ArrowBackIcon />}
              sx={{ ...ghostBtnSx, mb: 2, px: 1.5, py: 0.5 }}
            >
              {step === 'pay' ? 'Shipping' : 'All options'}
            </Button>

            {step === 'shipping' ? (
              <>
                <Typography
                  sx={{
                    ...kickerSx,
                    color: GOLD,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'center'
                  }}
                >
                  <LocalShippingOutlinedIcon sx={{ fontSize: 16 }} /> Where
                  should we ship it?
                </Typography>
                <Box
                  sx={{
                    mt: 2,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.75,
                    // Taller inputs than the default size="small" — easier to read + tap.
                    '& .MuiInputBase-input': { py: '14px' }
                  }}
                >
                  <TextField
                    label="Full name *"
                    value={shipping.name}
                    onChange={set('name')}
                    sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Email *"
                    value={shipping.email}
                    onChange={set('email')}
                    sx={fieldSx}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Phone *"
                    value={shipping.phone}
                    onChange={set('phone')}
                    sx={fieldSx}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Address line 1 *"
                    value={shipping.line1}
                    onChange={set('line1')}
                    sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Address line 2"
                    value={shipping.line2}
                    onChange={set('line2')}
                    sx={{ ...fieldSx, gridColumn: { sm: '1 / -1' } }}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="City / Suburb *"
                    value={shipping.city}
                    onChange={set('city')}
                    sx={fieldSx}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Postcode *"
                    value={shipping.postcode}
                    onChange={set('postcode')}
                    sx={fieldSx}
                    fullWidth
                    size="small"
                  />
                  {/* Country drives the State/Province options below. */}
                  <TextField
                    select
                    label="Country *"
                    value={shipping.country}
                    onChange={(e) =>
                      setShipping((s) => ({
                        ...s,
                        country: e.target.value,
                        state: '' // states differ per country — reset it
                      }))
                    }
                    sx={selectSx}
                    SelectProps={{ MenuProps: selectMenuProps }}
                    fullWidth
                    size="small"
                  >
                    {countries.map((c) => (
                      <MenuItem key={c.isoCode} value={c.isoCode}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  {states.length > 0 ? (
                    <TextField
                      select
                      label="State / Province *"
                      value={shipping.state}
                      onChange={set('state')}
                      sx={selectSx}
                      SelectProps={{ MenuProps: selectMenuProps }}
                      fullWidth
                      size="small"
                    >
                      {states.map((s) => (
                        <MenuItem key={s.isoCode} value={s.name}>
                          {s.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <TextField
                      label="State / Province"
                      value={shipping.state}
                      onChange={set('state')}
                      sx={fieldSx}
                      fullWidth
                      size="small"
                    />
                  )}
                </Box>
                <FormControlLabel
                  sx={{ mt: 1.5, ml: 0, alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      checked={packOnVideo}
                      onChange={(e) => setPackOnVideo(e.target.checked)}
                      sx={{ color: PT.textMuted, py: 0.5, '&.Mui-checked': { color: GOLD } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13.5, color: PT.textMuted, mt: '6px' }}>
                      Pack my order on video for INCRYPT's socials — I'm happy for the
                      unboxing of my parcel to be filmed and shared.
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={{ mt: 0.5, ml: 0, alignItems: 'center' }}
                  control={
                    <Checkbox
                      checked={saveAddressChecked}
                      onChange={(e) => setSaveAddressChecked(e.target.checked)}
                      sx={{
                        color: PT.textMuted,
                        py: 0.5,
                        '&.Mui-checked': { color: GOLD }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: 13.5, color: PT.textMuted }}>
                      {hasSavedAddress
                        ? 'Update my saved shipping address'
                        : 'Save my shipping address for next time'}
                    </Typography>
                  }
                />
              </>
            ) : (
              payment && (
                <>
                  <Typography sx={{ ...kickerSx, color: GOLD, mb: 2 }}>
                    Payment
                  </Typography>
                  <Elements
                    stripe={activeStripe}
                    options={{
                      clientSecret: payment.clientSecret,
                      appearance: {
                        theme: 'night',
                        variables: {
                          colorPrimary: PT.teal,
                          colorBackground: PT.surface,
                          colorText: PT.text,
                          borderRadius: '10px',
                          fontFamily: 'Montserrat, sans-serif'
                        }
                      }
                    }}
                  >
                    <PaymentForm
                      amount={payment.amount}
                      billing={{
                        name: shipping.name,
                        email: shipping.email,
                        phone: shipping.phone || undefined,
                        address: {
                          line1: shipping.line1,
                          line2: shipping.line2 || undefined,
                          city: shipping.city,
                          state: shipping.state || undefined,
                          postal_code: shipping.postcode,
                          country: shipping.country
                        }
                      }}
                      onBack={() => setStep('shipping')}
                      onDone={handlePaymentDone}
                    />
                  </Elements>
                </>
              )
            )}
          </Box>

          {/* Right: order summary. On mobile it's first on the payment step (top),
              and sits above the Continue button on the shipping step. */}
          <Box
            sx={{
              ...panelSx,
              p: 2.5,
              position: { md: 'sticky' },
              top: 16,
              gridColumn: { md: 2 },
              gridRow: { md: '1 / span 2' },
              order: { xs: step === 'pay' ? -1 : 0, md: 0 }
            }}
          >
            <Typography sx={{ ...kickerSx }}>Order summary</Typography>
            <Box
              sx={{ display: 'flex', gap: 1.5, mt: 1.75, alignItems: 'center' }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 74,
                  borderRadius: '8px',
                  flexShrink: 0,
                  border: `1px solid ${PT.border}`,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(255,255,255,0.03)',
                  overflow: 'hidden'
                }}
              >
                {selected.boxOnly ? (
                  <Inventory2Icon sx={{ color: GOLD }} />
                ) : (
                  <Box
                    component="img"
                    src={angelicPack}
                    alt=""
                    sx={{ height: 70, objectFit: 'contain' }}
                  />
                )}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800 }}>
                  {selected.name}
                </Typography>
                <Typography sx={{ color: PT.textMuted, fontSize: 12.5 }}>
                  Angelic Faction · {selected.tagline}
                </Typography>
              </Box>
            </Box>
            {/* Quantity */}
            <Box
              sx={{
                borderTop: `1px solid ${PT.border}`,
                mt: 2,
                pt: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ color: PT.textMuted }}>Quantity</Typography>
              {step === 'shipping' ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    border: `1px solid ${PT.border}`,
                    borderRadius: '10px',
                    px: 0.5
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label="Decrease quantity"
                    disabled={quantity <= 1}
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    sx={{ color: PT.text, width: 30, height: 30, fontSize: 20 }}
                  >
                    −
                  </IconButton>
                  <Typography
                    sx={{ minWidth: 26, textAlign: 'center', fontWeight: 800 }}
                  >
                    {quantity}
                  </Typography>
                  <IconButton
                    size="small"
                    aria-label="Increase quantity"
                    disabled={quantity >= maxQty}
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                    sx={{ color: PT.text, width: 30, height: 30, fontSize: 18 }}
                  >
                    +
                  </IconButton>
                </Box>
              ) : (
                <Typography sx={{ fontWeight: 800 }}>×{quantity}</Typography>
              )}
            </Box>
            {step === 'shipping' &&
              PACKS_PER[selected.id] > 0 &&
              maxQty < MAX_QTY &&
              quantity >= maxQty &&
              !!stock && (
                <Typography
                  sx={{
                    mt: 0.75,
                    color: PT.textMuted,
                    fontSize: 11.5,
                    textAlign: 'right'
                  }}
                >
                  Max for the {stock.remaining.toLocaleString()} pack
                  {stock.remaining === 1 ? '' : 's'} left
                </Typography>
              )}
            {/* Shipping (live Australia Post quote) */}
            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between'
              }}
            >
              <Typography sx={{ color: PT.textMuted, fontSize: 13 }}>
                Shipping{shippingQuote?.service ? ` · ${shippingQuote.service}` : ''}
              </Typography>
              <Typography sx={{ color: PT.text, fontSize: 13, fontWeight: 700 }}>
                {quoteLoading
                  ? 'Calculating…'
                  : shippingQuote
                    ? shippingQuote.cost > 0
                      ? aud(shippingQuote.cost)
                      : 'Free'
                    : '—'}
              </Typography>
            </Box>
            {/* Total */}
            <Box
              sx={{
                borderTop: `1px solid ${PT.border}`,
                mt: 2,
                pt: 2,
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography sx={{ color: PT.textMuted }}>Total (AUD)</Typography>
                {quantity > 1 && (
                  <Typography sx={{ color: PT.textMuted, fontSize: 11.5 }}>
                    {quantity} × {aud(selected.price)}
                    {shippingQuote?.cost
                      ? ` + ${aud(shippingQuote.cost)} shipping`
                      : ''}
                  </Typography>
                )}
              </Box>
              <Typography sx={{ fontWeight: 900, fontSize: 20, color: GOLD }}>
                {aud(
                  step === 'pay' && payment
                    ? payment.amount
                    : selected.price * quantity + (shippingQuote?.cost || 0)
                )}
              </Typography>
            </Box>
            <Typography
              sx={{
                mt: 2,
                color: PT.textMuted,
                fontSize: 11.5,
                lineHeight: 1.5
              }}
            >
              Pre-purchase — ships when the first print run is ready. You'll get
              an email receipt at checkout.
            </Typography>
          </Box>

          {/* Continue button in its own grid cell: on mobile it lands UNDER the order
              summary (shipping step); on desktop it sits under the form in column 1. */}
          {step === 'shipping' && (
            <Box sx={{ gridColumn: { md: '1' } }}>
              {formError && (
                <Typography sx={{ mb: 1.5, color: '#ff8a9b', fontSize: 13 }}>
                  {formError}
                </Typography>
              )}
              <Button
                onClick={submitShipping}
                disabled={submitting}
                sx={{ ...primaryBtnSx, py: 1.1, width: '100%' }}
              >
                {submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Continue to payment'
                )}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* ── Step: done ───────────────────────────────────────────────────────── */}
      {step === 'done' && selected && (
        <Box
          sx={{
            ...panelSx,
            p: { xs: 3, md: 5 },
            textAlign: 'center',
            maxWidth: 560,
            mx: 'auto'
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 64, color: GOLD }} />
          <Typography sx={{ mt: 1.5, fontWeight: 900, fontSize: 24 }}>
            Pre-purchase confirmed
          </Typography>
          <Typography sx={{ mt: 1.5, color: PT.textMuted }}>
            Thank you for purchasing{' '}
            <b style={{ color: PT.text }}>
              {quantity} × {selected.name}
            </b>{' '}
            of our physical cards. We will get them to you as soon as we can — it
            will take 4–6 weeks. We will send an email to{' '}
            <b style={{ color: PT.text }}>{shipping.email}</b> when we have
            shipped your physical cards.
          </Typography>
          <Button
            onClick={() => {
              setStep('choose');
              setSelectedId(null);
              setPayment(null);
            }}
            sx={{ ...ghostBtnSx, mt: 3, px: 3 }}
          >
            Back to store
          </Button>
        </Box>
      )}
    </Box>
  );
};

/* Pure, presentational pieces of the storefront, shared with the PUBLIC homepage
   version (features/PhysicalCards/PublicPhysicalCards). Re-exported (not moved) so
   the member page is otherwise untouched — the product catalog, pack visuals and
   the Stripe payment step stay a single source of truth across both. */
export {
  GOLD,
  GOLD_SOFT,
  GOLD_BORDER,
  aud,
  OPTIONS,
  PACKS_PER,
  MAX_QTY,
  FEATURES,
  Tilt,
  PackFan,
  PaymentForm
};
export type { OptionId, Shipping };

export default PhysicalCards;
